import { ServerCrash, RefreshCw } from "lucide-react";
import { isDebugMode } from "../../lib/debug";
import { useTheme } from "../../utils/ThemeProvider";
import { cn } from "../../lib/utils";
import { ErrorPage, ErrorActionButton } from "./ErrorPage";

function toErrorDisplayString(error) {
  if (error == null) return null;
  const msg = error?.message ?? error;
  if (typeof msg === "string") return msg;
  if (msg && typeof msg === "object" && Array.isArray(msg.errors)) {
    return msg.errors.flat().join(" ");
  }
  if (msg && typeof msg === "object" && msg.errors && typeof msg.errors === "object") {
    return Object.values(msg.errors).flat().join(" ");
  }
  try {
    return String(msg);
  } catch {
    return null;
  }
}

export function ServerError({ error, onRetry }) {
  const { isDark } = useTheme();
  const detail = isDebugMode ? toErrorDisplayString(error) : null;
  const stack = typeof error?.stack === "string" ? error.stack : "";

  const handleRetry =
    onRetry ||
    (() => {
      window.location.reload();
    });

  return (
    <ErrorPage
      code="500"
      title="Something went wrong"
      description="We hit an unexpected problem. Try reloading the page. If it keeps happening, contact your zone administrator."
      icon={ServerCrash}
      primaryAction={
        <ErrorActionButton onClick={handleRetry}>
          <RefreshCw className="h-4 w-4" />
          Reload page
        </ErrorActionButton>
      }
    >
      {detail && (
        <div
          className={cn(
            "mt-5 rounded-xl px-4 py-3 text-left text-sm border",
            isDark
              ? "border-red-400/30 bg-red-500/15 text-red-100"
              : "border-red-200 bg-red-50 text-red-700"
          )}
        >
          {detail}
        </div>
      )}
      {isDebugMode && stack && (
        <details
          className={cn(
            "mt-4 text-left rounded-xl p-4 text-xs font-mono overflow-auto max-h-48 border",
            isDark ? "border-white/15 bg-black/30 text-white/70" : "border-border bg-muted text-muted-foreground"
          )}
        >
          <summary className="cursor-pointer mb-2 font-sans text-sm">Error details</summary>
          <pre className="whitespace-pre-wrap">{stack}</pre>
        </details>
      )}
    </ErrorPage>
  );
}

export default ServerError;
